<template>
    <RegularModal ref="modal">
        <template v-slot:title>
            {{ translate("admin_schedules_list.estimating_fights_time") }}
        </template>

        <template v-slot:body>
            <div class="columns is-centered is-mobile">
                <div v-if="isLoading">
                    <LoadingSpinner2 class="has-added-margin" />
                </div>
                <div class="column is-full" v-else-if="dataLoaded">
                    <p class="title is-6">{{ translate("admin_schedules_list.estimate_explanation") }}</p>
                    <div class="field">
                        <label class="label">{{ translate("admin_schedules_list.planned_no_of_mats") }}</label>
                        <input v-model="numberOfMatsForStats" class="input" type="text" placeholder="" />
                    </div>
                    <p>{{ translate("admin_schedules_list.number_of_fights") }}: {{ totalFightsPerMat }}</p>
                    <p>{{ translate("admin_schedules_list.time_of_fights") }}: {{ totalTimePerMat }}</p>
                </div>
            </div>
        </template>

        <template v-slot:footer>
            <button @click="close" class="button">
                {{ translate("common.close") }}
            </button>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { computed, ref } from "vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { useModal } from "../Common/useModal";
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import timeFormatter from "../Common/timeFormatter";
    import fightsApi from "../Api/fights";

    const props = defineProps<{
        eventId: number;
    }>();

    const { modal, open: openModal, close: closeModal } = useModal();

    const isLoading = ref(false);
    const dataLoaded = ref(false);
    const numberOfMatsForStats = ref("1");
    const totalFights = ref(0);
    const totalTimeInSeconds = ref(0);

    const totalTimePerMat = computed(() => {
        const parsedNumberOfMats = parseInt(numberOfMatsForStats.value, 10);
        const totalMats = parsedNumberOfMats > 0 ? parsedNumberOfMats : 1;
        const totalSecondsForMat = totalTimeInSeconds.value / totalMats;

        return timeFormatter.formatSecondsToPrettyTime(totalSecondsForMat);
    });

    const totalFightsPerMat = computed(() => {
        const parsedNumberOfMats = parseInt(numberOfMatsForStats.value, 10);
        const totalMats = parsedNumberOfMats > 0 ? parsedNumberOfMats : 1;

        return Math.floor(totalFights.value / totalMats);
    });

    function open() {
        numberOfMatsForStats.value = "1";
        openModal();
        getTotalFightsTime();
    }

    function close() {
        dataLoaded.value = false;
        closeModal();
    }

    function getTotalFightsTime() {
        isLoading.value = true;

        return fightsApi
            .getApproximateSchedulesTime(props.eventId)
            .then(resp => {
                totalFights.value = resp.totalFights;
                totalTimeInSeconds.value = resp.totalTimeInSeconds;
            })
            .catch(handleError)
            .finally(() => {
                isLoading.value = false;
                dataLoaded.value = true;
            });
    }

    defineExpose({ open });
</script>
